<!-- WebhookEventNotification.svelte -->
<script>
  import { onMount } from 'svelte';
  import { pendingWebhookEvents, autoSyncOnLogin } from '../stores/authStore';
  
  let showNotification = false;
  let syncing = false;
  let syncResult = null;
  let showResult = false;
  let currentBatch = 0;
  let totalBatches = 0;
  let syncProgress = null;
  let autoSyncTriggered = false;
  
  // Subscribe to pending events count
  $: showNotification = $pendingWebhookEvents > 0 || showResult;

  // Auto-sync on mount if user has the preference enabled and there are pending events
  $: if ($autoSyncOnLogin && $pendingWebhookEvents > 0 && !syncing && !autoSyncTriggered) {
    autoSyncTriggered = true;
    handleSyncClick();
  }
  
  async function handleSyncClick() {
    syncing = true;
    syncResult = null;
    showResult = false;
    currentBatch = 0;
    totalBatches = 0;
    syncProgress = null;
    
    const batchSize = 50;
    let batchNumber = 1;
    let hasMore = true;
    let totalProcessed = 0;
    let totalFailed = 0;
    let allResults = [];
    
    try {
      console.log('🔄 Starting webhook sync process with batching...');
      
      // Process batches until no more events remain
      while (hasMore) {
        currentBatch = batchNumber;
        syncProgress = `Processing batch ${batchNumber}...`;
        
        console.log(`📦 Processing batch ${batchNumber} (${batchSize} events max)...`);
        
        const response = await fetch('/api/webhooks/process', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            batchSize: batchSize,
            batchNumber: batchNumber
          })
        });
        
        // Check if response is JSON before trying to parse it
        const contentType = response.headers.get('content-type');
        let data;
        
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          // If not JSON, try to get text (might be HTML error page)
          const text = await response.text();
          console.error('❌ Non-JSON response:', text.substring(0, 200));
          throw new Error('Server returned non-JSON response. Please check if you are logged in and try again.');
        }
        
        if (!response.ok || !data.success) {
          console.error('❌ Batch sync failed:', data);
          throw new Error(data.message || data.error || 'Failed to process webhook events');
        }
        
        console.log(`✅ Batch ${batchNumber} completed:`, data);
        
        // Accumulate results
        totalProcessed += data.processed || 0;
        totalFailed += data.failed || 0;
        allResults = allResults.concat(data.results || []);
        
        // Check rate limit info and pause if needed
        const rateLimitInfo = data.rateLimitInfo || {};
        if (rateLimitInfo.isThrottled) {
          const pauseMs = rateLimitInfo.recommendedPause || 5000;
          console.warn(`⚠️  Rate limit throttling detected, pausing ${pauseMs}ms before next batch...`);
          syncProgress = `Rate limited - pausing ${pauseMs/1000}s...`;
          await new Promise(resolve => setTimeout(resolve, pauseMs));
        }
        
        // Check if there are more batches
        hasMore = data.hasMore || false;
        
        if (hasMore) {
          batchNumber++;
          // Estimate total batches
          totalBatches = batchNumber + Math.ceil(data.remaining / batchSize);
          console.log(`⏳ ${data.remaining} events remaining, starting next batch...`);
          
          // Add a minimal pause between batches even when not throttled
          if (!rateLimitInfo.isThrottled) {
            await new Promise(resolve => setTimeout(resolve, 100)); // Just 100ms when not throttled
          }
        } else {
          totalBatches = batchNumber;
          console.log(`🎉 All batches complete!`);
        }
      }
      
      // All batches completed successfully
      syncResult = {
        type: 'success',
        message: totalBatches > 1 
          ? `Successfully processed ${totalProcessed} events across ${totalBatches} batches`
          : `Successfully processed ${totalProcessed} events`,
        processed: totalProcessed,
        failed: totalFailed,
        total: totalProcessed + totalFailed,
        batches: totalBatches,
        results: allResults,
        note: 'Not all events from Xero need to be sent to JobTread, 0 events processed is often completely normal.'
      };
      
      // Update pending count (should be 0 or only failed events)
      pendingWebhookEvents.set(totalFailed);
      
      showResult = true;
      syncProgress = null;
      
      // Auto-hide success message after 4 seconds
      setTimeout(() => {
        if (showResult) {
          showResult = false;
          syncResult = null;
        }
      }, 8000);
      
    } catch (error) {
      console.error('💥 Sync error:', error);
      
      syncResult = {
        type: 'error',
        message: 'Network error while processing webhooks',
        details: error.message
      };
      
      showResult = true;
      syncProgress = null;
    } finally {
      syncing = false;
      currentBatch = 0;
      totalBatches = 0;
    }
  }
  
  function dismissNotification() {
    showResult = false;
    syncResult = null;
    if ($pendingWebhookEvents === 0) {
      showNotification = false;
    }
  }

  async function toggleAutoSync(event) {
    const checked = event.target.checked;
    try {
      const response = await fetch('/api/users/auto-sync-on-login', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoSyncOnLogin: checked })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        autoSyncOnLogin.set(checked);
      } else {
        event.target.checked = !checked;
      }
    } catch {
      event.target.checked = !checked;
    }
  }
</script>

{#if showNotification}
  {#if syncResult}
    <!-- Compact result bar (success / error) -->
    <div class="{syncResult.type === 'success' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'} border rounded-lg px-3 py-2.5 mb-4" role="alert">
      <div class="flex items-start gap-2.5">
        <div class="flex-shrink-0 mt-0.5">
          {#if syncResult.type === 'success'}
            <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          {:else}
            <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          {/if}
        </div>
        <div class="flex-1 min-w-0 text-sm {syncResult.type === 'success' ? 'text-green-800' : 'text-red-800'}">
          <p class="font-semibold">{syncResult.type === 'success' ? 'Sync completed' : 'Sync failed'}</p>
          <p class="text-xs mt-0.5 opacity-90">{syncResult.message}</p>
          {#if syncResult.type === 'success' && syncResult.failed > 0}
            <p class="text-xs mt-1 text-orange-700">{syncResult.failed} {syncResult.failed === 1 ? 'update was' : 'updates were'} not applied to JobTread — usually old invoices, safe to ignore.</p>
          {/if}
          {#if syncResult.details}
            <p class="text-xs mt-1 opacity-80">{syncResult.details}</p>
          {/if}
        </div>
      </div>
    </div>
  {:else}
    <!-- Compact "Auto Sync" bar -->
    <div class="bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 mb-4" role="alert">
      <div class="flex items-center gap-x-2 gap-y-2 flex-wrap">
        <svg class="flex-shrink-0 h-4 w-4 text-yellow-500 {syncing ? 'animate-spin' : ''}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span class="font-semibold text-yellow-800 text-sm">Auto Sync</span>
        <span class="text-yellow-700 text-xs whitespace-nowrap">· {$pendingWebhookEvents} {$pendingWebhookEvents === 1 ? 'change' : 'changes'} pending</span>

        <div class="ml-auto flex items-center gap-2 sm:gap-3">
          <label class="inline-flex items-center cursor-pointer select-none" title="Sync automatically on login">
            <input
              type="checkbox"
              checked={$autoSyncOnLogin}
              on:change={toggleAutoSync}
              class="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
            />
            <span class="ml-1.5 text-xs text-yellow-800 hidden sm:inline">Auto on login</span>
          </label>
          <button
            type="button"
            on:click={handleSyncClick}
            disabled={syncing}
            class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {#if syncing}
              {syncProgress ? syncProgress : totalBatches > 0 ? `Batch ${currentBatch}/~${totalBatches}` : 'Syncing…'}
            {:else}
              Sync now
            {/if}
          </button>
          <button
            type="button"
            on:click={dismissNotification}
            class="flex-shrink-0 text-yellow-500 hover:text-yellow-700 rounded p-1 focus:outline-none"
            aria-label="Dismiss"
          >
            <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  {/if}
{/if}

<style>
  /* Additional custom styles if needed */
</style>


